import { useCallback, useEffect, useState } from "react";
import { UserPlus, Search, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { ContactTable } from "@/components/crm/ContactTable";
import { EditContactDialog } from "@/components/crm/EditContactDialog";
import { crmService, emitCrmChanged } from "@/services/crmService";
import { useCrmActions } from "@/context/CrmActionsContext";
import { CONTACT_TABS, CONTACT_SORTS } from "@/config/crmOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ALL = "__all__";

export default function CRM() {
  const { openAddContact, openNewLead } = useCrmActions();
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [source, setSource] = useState(ALL);
  const [tag, setTag] = useState(ALL);
  const [sort, setSort] = useState("name");
  const [contacts, setContacts] = useState([]);
  const [sources, setSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { contacts } = await crmService.listContacts({
        tab, search: search || undefined, status,
        source: source === ALL ? undefined : source,
        tag: tag === ALL ? undefined : tag,
        sort,
      });
      setContacts(contacts);
    } catch (e) {
      toast.error("Could not load contacts");
    } finally {
      setLoading(false);
    }
  }, [tab, search, status, source, tag, sort]);

  useEffect(() => {
    crmService.listSources().then(setSources).catch(() => {});
    crmService.listTags().then(setTags).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load]);

  const archive = async (c) => {
    try { await crmService.archiveContact(c.id); toast.success("Contact archived"); emitCrmChanged(); }
    catch { toast.error("Could not archive"); }
  };
  const restore = async (c) => {
    try { await crmService.restoreContact(c.id); toast.success("Contact restored"); emitCrmChanged(); }
    catch { toast.error("Could not restore"); }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="crm-page">
        <PageHeader
          eyebrow="AgentHQ · Sell"
          title="CRM"
          description="Your master relationship database — every client, lead, referral partner and contact in one place."
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate("/follow-ups")} data-testid="crm-followups">
                <CalendarClock className="h-4 w-4" /> Follow-Ups
              </Button>
              <Button onClick={openAddContact} data-testid="crm-add-contact">
                <UserPlus className="h-4 w-4" /> Add Contact
              </Button>
            </div>
          }
        />

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-border" data-testid="crm-tabs">
          {CONTACT_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`crm-tab-${t.key}`}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === t.key ? "border-brand-gold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex min-w-[200px] flex-1 items-center gap-2 rounded-lg border border-border px-3">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone…"
              className="border-0 px-0 shadow-none focus-visible:ring-0" data-testid="crm-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px]" data-testid="crm-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-[150px]" data-testid="crm-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All sources</SelectItem>
              {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tag} onValueChange={setTag}>
            <SelectTrigger className="w-[140px]" data-testid="crm-filter-tag"><SelectValue placeholder="Tag" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All tags</SelectItem>
              {tags.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[160px]" data-testid="crm-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTACT_SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="py-10"><LoadingState label="Loading contacts" /></div>
        ) : contacts.length === 0 ? (
          <EmptyState
            title="Your relationships start here."
            description="Add your first client, lead, referral partner, or professional contact."
            action={<Button className="mt-5" onClick={openAddContact} data-testid="crm-empty-add"><UserPlus className="h-4 w-4" /> Add Contact</Button>}
          />
        ) : (
          <ContactTable
            contacts={contacts}
            onEdit={(c) => setEditing(c)}
            onArchive={archive}
            onRestore={restore}
            onNewLead={(c) => openNewLead({ id: c.id, first_name: c.first_name, last_name: c.last_name, email: c.email })}
          />
        )}
      </div>

      <EditContactDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        contact={editing}
        onSaved={() => { setEditing(null); emitCrmChanged(); }}
      />
    </AppShell>
  );
}
